import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DateLocalePipe, FIRST_ELEMENT_INDEX } from '@modeso/aumico-lib-core-fe';
import { IAnnualReportDto, ReportType } from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GeneralInformationMode } from '../../../enums/general-information-form-mode.enum';
import { ISheetDates } from '../../../interfaces/sheets-dates.interface';
import { DatesCalculatorUtil } from '../../../utils/dates.util';
import { GeneralInformationBaseComponent } from '../base/general-information-base.component';

@UntilDestroy()
@Component({
    selector: 'aumico-general-information-autofill',
    templateUrl: '../general-information-v2.component.html',
    styleUrls: ['../general-information-v2.component.scss'],
})
export class GeneralInformationAutofillComponent extends GeneralInformationBaseComponent implements OnInit, OnChanges {
    get mode(): GeneralInformationMode {
        return GeneralInformationMode.AUTOFILL;
    }

    // Getters specific to AUTOFILL mode
    get showAutofillFromPreviousForBalanceToggle(): boolean {
        return this.previousAnnualReportOptions?.length > 0;
    }

    get showAutofillFromPreviousForIncomeToggle(): boolean {
        return this.previousAnnualReportOptions?.length > 0;
    }

    get showBalanceSheetReportSelection(): boolean {
        return (
            this.form?.get('autofillFromPreviousForBalance')?.value === true &&
            this.previousAnnualReportOptions?.length > 0
        );
    }

    get showIncomeStatementReportSelection(): boolean {
        return (
            this.form?.get('autofillFromPreviousForIncome')?.value === true &&
            this.previousAnnualReportOptions?.length > 0
        );
    }

    constructor(formBuilder: FormBuilder, dateLocalePipe: DateLocalePipe) {
        super(formBuilder, dateLocalePipe);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.setupAutofillSpecificSubscriptions();
        this.initializeAutofillMode();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['previousAnnualReportOptions']
        ) {
            this.prepareSelectReportOptions();

        }

        if (
            changes['previousAnnualReportOptions'] &&
            changes['previousAnnualReportOptions'].currentValue?.length > 0 &&
            this.accountingBases.length > 0 &&
            this.arTemplates.length > 0
        ) {
            this.initializeAutofillMode();
        }

        if (changes['accountingBases'] && changes['accountingBases'].currentValue?.length > 0 && this.previousAnnualReportOptions?.length > 0) {
            if (this.form) {
                const latestReport = this.getLatestReportByType(this.setupData?.reportType as ReportType);
                this.preSelectAccountingBasisFromReport(latestReport);
            } else if (this.arTemplates.length > 0) {
                this.initializeAutofillMode();
            }
        }

        // If form has been initialized and arTemplates input changes with data, re-patch arTemplate
        if (changes['arTemplates'] && this.arTemplates.length > 0 && this.previousAnnualReportOptions?.length > 0) {
            if (this.form) {
                const latestReport = this.getLatestReportByType(this.setupData?.reportType as ReportType);
                this.preSelectArTemplateFromReport(latestReport);
            } else if (this.accountingBases.length > 0) {
                this.initializeAutofillMode();
            }
        }

        // If form has been initialized and reportLanguages input changes with data, re-patch locale
        if (
            changes['reportLanguages'] &&
            this.reportLanguages.length > 0 &&
            this.previousAnnualReportOptions?.length > 0
        ) {
            if (this.form) {
                const latestReport = this.getLatestReportByType(this.setupData?.reportType as ReportType);
                this.preSelectLocaleFromReport(latestReport);
            }
        }

        // If form has been initialized and currencies input changes with data, re-patch currency
        if (
            changes['currencies'] &&
            this.currencies.length > 0 &&
            this.previousAnnualReportOptions?.length > 0
        ) {
            if (this.form && this.setupData?.reportType) {
                const latestReport = this.getLatestReportByType(this.setupData?.reportType as ReportType);
                if (latestReport) {
                    this.preSelectCurrencyFromReport(latestReport);
                }
            }
        }
    }



    private setupAutofillSpecificSubscriptions(): void {

        // Autofill balance sheet report toggle changes
        this.form
            .get('autofillFromPreviousForBalance')
            ?.valueChanges.pipe(untilDestroyed(this))
            .subscribe((enabled: boolean) => {
                if (!enabled) {
                    this.form.patchValue({ autofillReportForBalance: null });
                } else if (this.form.get('autofillReportForBalance')?.value == null) {
                    this.form.patchValue({ autofillReportForBalance: this.defaultAutofillReport('balance') });
                }
            });

        // Autofill income statement report toggle changes
        this.form
            .get('autofillFromPreviousForIncome')
            ?.valueChanges.pipe(untilDestroyed(this))
            .subscribe((enabled: boolean) => {
                if (!enabled) {
                    this.form.patchValue({ autofillReportForIncome: null });
                } else if (this.form.get('autofillReportForIncome')?.value == null) {
                    this.form.patchValue({ autofillReportForIncome: this.defaultAutofillReport('income') });
                }
            });

        // Autofill Balance Report Selection changes
        this.form
            .get('autofillReportForBalance')
            ?.valueChanges.pipe(untilDestroyed(this))
            .subscribe(() => {
                this.reinitializeAutofillModeWithSelectedReports();
            });

        // Autofill Income Statement Report Selection changes
        this.form
            .get('autofillReportForIncome')
            ?.valueChanges.pipe(untilDestroyed(this))
            .subscribe(() => {
                this.reinitializeAutofillModeWithSelectedReports();
            });
    }

    // Returns the report one side starts out with: the newest prior report that fits the report type
    private defaultAutofillReport(which: 'balance' | 'income'): INameId | null {
        const options = which === 'balance' ? this.balanceSheetReportOptions : this.incomeStatementReportOptions;
        if (!options?.length) {
            return null;
        }

        const latestReport = this.getLatestReportByType(this.setupData?.reportType as ReportType);
        return options.find((report) => report.id === (latestReport as any)?.id) ?? options[FIRST_ELEMENT_INDEX];
    }

    // Tells whether the end of an autofill report's period lies before the new report's balance sheet date
    private periodEndPrecedesCurrentPeriod(periodEnd: string | undefined): boolean {
        if (periodEnd == null || this.setupData?.balanceSheetDate == null) {
            return false;
        }

        return new Date(periodEnd).getTime() < new Date(this.setupData.balanceSheetDate).getTime();
    }

    // The balance autofill report hands over its balance sheet date, so that is its period end
    private balanceReportPrecedesCurrentPeriod(autofillBalanceReport: Partial<IAnnualReportDto>): boolean {
        return this.periodEndPrecedesCurrentPeriod(autofillBalanceReport.generalInformation?.balanceSheetDate);
    }

    // The income autofill report hands over its income statement period, so that period's end counts
    private incomeReportPrecedesCurrentPeriod(autofillIncomeReport: Partial<IAnnualReportDto>): boolean {
        return this.periodEndPrecedesCurrentPeriod(autofillIncomeReport.generalInformation?.incomeCurrentYearToDate);
    }

    // Tells whether the report type is one of the interim types
    private isInterimReportType(reportType: ReportType | undefined): boolean {
        return (
            reportType === ReportType.QUARTERLY ||
            reportType === ReportType.MONTHLY ||
            reportType === ReportType.CUSTOM
        );
    }

    // Returns the sheet dates, taking the previous period from the autofill reports only when they precede ours
    private resolveSheetDates(
        autofillBalanceReport: Partial<IAnnualReportDto> | undefined,
        autofillIncomeReport: Partial<IAnnualReportDto> | undefined,
    ): ISheetDates {
        const isAnnualReport = this.setupData.reportType == null || this.setupData.reportType === ReportType.ANNUAL;
        // Balance and income report are picked independently, so both are checked
        const isAutofillFromInterimReport =
            this.isInterimReportType(autofillBalanceReport?.setupData?.reportType) ||
            this.isInterimReportType(autofillIncomeReport?.setupData?.reportType);

        // An annual report never inherits an interim period, and no report inherits a period that does not precede it
        const deriveFromOwnDates =
            autofillBalanceReport == null ||
            autofillIncomeReport == null ||
            (isAnnualReport && isAutofillFromInterimReport) ||
            !this.balanceReportPrecedesCurrentPeriod(autofillBalanceReport) ||
            !this.incomeReportPrecedesCurrentPeriod(autofillIncomeReport);

        if (deriveFromOwnDates) {
            return DatesCalculatorUtil.calculateFinancialStatementDates(
                this.setupData.balanceSheetDate,
                this.setupData.reportType as ReportType,
            );
        }

        return DatesCalculatorUtil.calculateFinancialStatementDatesFromAutofillDates(
            this.setupData.balanceSheetDate,
            this.setupData.reportType as ReportType,
            autofillBalanceReport.generalInformation.balanceSheetDate,
            autofillIncomeReport.generalInformation.incomeCurrentYearFromDate,
            autofillIncomeReport.generalInformation.incomeCurrentYearToDate,
            autofillIncomeReport.setupData?.reportType || ReportType.ANNUAL,
        );
    }

    private initializeAutofillMode(): void {
        if (!this.setupData) return;

        const latestReport = this.getLatestReportByType(this.setupData?.reportType as ReportType);
        const sheetDates: ISheetDates = this.resolveSheetDates(latestReport, latestReport);



        if (this.previousAnnualReportOptions?.length !== 0) {
            // Set all relevant fields to disabled/enabled in AUTOFILL mode
            this.disableCompany = true;
            this.disableAccountingBasis = false;
            this.disableArTemplate = false;
            this.disableLanguage = false;
            this.disablePreviousSettings = false;
            if (this.setupData.reportType === ReportType.ANNUAL) {
                this.disableReportType = true;
            } else {
                this.disableReportType = false;
            }

            // Pre-select company from setup data, only when it is not already selected
            const preselectedCompany = this.companies.find((c) => c._id === this.setupData?.company._id);
            if (preselectedCompany && this.form.get('company')?.value?._id !== preselectedCompany._id) {
                this.form.patchValue({ company: preselectedCompany });
            }

            // Pre-select from latest previous annual report
            this.preSelectFromLatestReport();

            // Pre-select report type from setup data
            const preselectedReportType = this.reportTypes.find((rt) => rt.id === this.setupData?.reportType);
            if (preselectedReportType) {
                this.form.patchValue({ reportType: preselectedReportType }, { emitEvent: false });
            }

            // Enable toggles and pre-select reports and dates if autofillData exists
            this.form.patchValue({
                // current year dates
                balanceSheetDate: sheetDates.balanceCurrentYearDate,
                incomeCurrentYearFromDate: sheetDates.incomeCurrentYearFromDate,
                incomeCurrentYearToDate: sheetDates.incomeCurrentYearToDate,
                // previous year dates and data
                autofillFromPreviousForBalance: true,
                balancePreviousYearDate:
                    sheetDates.balancePreviousYearDate,
                autofillReportForBalance: this.balanceSheetReportOptions.find(report => report.id === (latestReport as any).id) || this.balanceSheetReportOptions[FIRST_ELEMENT_INDEX],
                autofillFromPreviousForIncome: true,
                incomePreviousYearFromDate:
                    sheetDates.incomePreviousYearFromDate,
                incomePreviousYearToDate:
                    sheetDates.incomePreviousYearToDate,
                autofillReportForIncome: this.incomeStatementReportOptions.find(report => report.id === (latestReport as any).id) || this.incomeStatementReportOptions[FIRST_ELEMENT_INDEX],
            });
        } else {
            // No previous data available
            this.form.patchValue({
                autofillFromPreviousForBalance: false,
                balancePreviousYearDate: sheetDates.balancePreviousYearDate,
                autofillReportForBalance: null,
                autofillFromPreviousForIncome: false,
                incomePreviousYearFromDate: sheetDates.incomePreviousYearFromDate,
                incomePreviousYearToDate: sheetDates.incomePreviousYearToDate,
                autofillReportForIncome: null,
            });
        }

        // Update form control states after setting disabled flags
        this.updateFormControlStates();
    }

    private preSelectFromLatestReport(): void {
        const latestReport = this.getLatestReportByType(this.setupData?.reportType as ReportType);

        this.preSelectAccountingBasisFromReport(latestReport);

        this.preSelectArTemplateFromReport(latestReport);

        this.preSelectLocaleFromReport(latestReport);

        this.preSelectCurrencyFromReport(latestReport);

    }

    private reinitializeAutofillModeWithSelectedReports(): void {
        if (this.previousAnnualReportOptions?.length === 0) return;

        const correspondingPreviousBalanceReport = this.previousAnnualReportOptions.find(
            (report: any) => report.id === this.form.get('autofillReportForBalance')?.value?.id,
        );

        const correspondingPreviousIncomeReport = this.previousAnnualReportOptions.find(
            (report: any) => report.id === this.form.get('autofillReportForIncome')?.value?.id,
        );

        if (!correspondingPreviousBalanceReport || !correspondingPreviousIncomeReport) return;

        const sheetDates: ISheetDates = this.resolveSheetDates(
            correspondingPreviousBalanceReport,
            correspondingPreviousIncomeReport,
        );

        // Pre-select from corresponding report
        this.preSelectFromSelectedReport(correspondingPreviousBalanceReport);

        // Update dates based on selected reports
        this.form.patchValue({
            // current year dates
            balanceSheetDate: sheetDates.balanceCurrentYearDate,
            incomeCurrentYearFromDate: sheetDates.incomeCurrentYearFromDate,
            incomeCurrentYearToDate: sheetDates.incomeCurrentYearToDate,
            // previous year dates and data
            autofillFromPreviousForBalance: true,
            balancePreviousYearDate: sheetDates.balancePreviousYearDate,
            autofillFromPreviousForIncome: true,
            incomePreviousYearFromDate: sheetDates.incomePreviousYearFromDate,
            incomePreviousYearToDate: sheetDates.incomePreviousYearToDate,
        });
    }

    private preSelectFromSelectedReport(report: Partial<IAnnualReportDto>): void {
        // Pre-select accounting basis from selected report
        this.preSelectAccountingBasisFromReport(report);


        // Pre-select ar template from selected report
        this.preSelectArTemplateFromReport(report);

        // Pre-select locale from selected report
        this.preSelectLocaleFromReport(report);


        // Pre-select report type from setup data
        const preselectedReportType = this.reportTypes.find((rt) => rt.id === this.setupData?.reportType);
        if (preselectedReportType) {
            this.form.patchValue({ reportType: preselectedReportType });
        }

        // Pre-select currency from selected report
        this.preSelectCurrencyFromReport(report);
    }

    private preSelectAccountingBasisFromReport(report: Partial<IAnnualReportDto>): void {

        const preselectedAccountingBasis = this.accountingBases.find(
            (ab) => ab.id === report.generalInformation.accountingBasis._id,
        );
        if (!preselectedAccountingBasis) {
            return;
        }
        // REV-21416: only re-patch when the accounting basis actually changes. Re-patching the SAME
        // value still emits valueChanges → the base component's onAccountingBasisChange resets arTemplate
        // (and locale) to null. When the autofill source report is changed but the basis is unchanged
        // (both prior reports share the same basis), that spurious reset would wipe a user-selected
        // arTemplate and let it be re-preselected from the report — defeating the preservation guard in
        // preSelectArTemplateFromReport. Skipping the no-op patch keeps the current selection intact.
        const currentAccountingBasis = this.form.get('accountingBasis')?.value as INameId | null;
        if (currentAccountingBasis?.id === preselectedAccountingBasis.id) {
            return;
        }
        this.form.patchValue({ accountingBasis: preselectedAccountingBasis });
    }

    private preSelectArTemplateFromReport(report: Partial<IAnnualReportDto>): void {
        if (report.annualReportTemplate == null) {
            return;
        }
        // REV-21416: preserve an existing arTemplate selection that still exists in the available
        // options. Autofill preselects the prior report's template on first render (arTemplate is null),
        // but once a value is set — whether auto-preselected or manually chosen by the user — changing
        // the balance/income autofill "Select report" must NOT overwrite it as long as it is still a
        // valid option. Only re-preselect when there is no current (valid) selection to protect.
        const currentArTemplate = this.form.get('arTemplate')?.value as INameId | null;
        if (currentArTemplate != null && this.arTemplates.some((at) => at.id === currentArTemplate.id)) {
            return;
        }
        const preselectedArTemplate = this.arTemplates.find((at) => at.id === report.annualReportTemplate._id);
        if (preselectedArTemplate) {
            this.form.patchValue({ arTemplate: preselectedArTemplate });
        }
    }

    private preSelectLocaleFromReport(report: Partial<IAnnualReportDto>): void {
        const preselectedLocale = this.reportLanguages.find((l) => l.id === report.locale);
        if (preselectedLocale) {
            this.form.patchValue({ locale: preselectedLocale });
        }
    }

    private preSelectCurrencyFromReport(report: Partial<IAnnualReportDto>): void {
        const preselectedCurrency = this.currencies.find((c) => c.id === report.generalInformation.currency._id);
        if (preselectedCurrency) {
            this.form.patchValue({ currency: preselectedCurrency });
        }

        // Cleared when the source has none, so switching autofill source does not leave behind a
        // presentation currency the user never picked for this report.
        const displayCurrencyId = report.generalInformation.displayCurrency?._id;
        this.form.patchValue({
            displayCurrency: displayCurrencyId ? this.currencies.find((c) => c.id === displayCurrencyId) : null,
        });
    }

    /**
 * Gets the latest report from previousAnnualReportOptions based on conditions.
 * If the selected report type is 'annual', then returns the latest annual report.
 * A report is considered 'annual' if the year of balanceSheetDate is different from the year of balancePreviousYearDate.
 * Otherwise, it's considered an interim report.
 */
    private getLatestReportByType(selectedReportType: ReportType): Partial<IAnnualReportDto> | undefined {
        if (!this.previousAnnualReportOptions?.length) {
            return undefined;
        }

        // Helper function to sort reports by most recent balanceSheetDate
        const sortByBalanceSheetDate = (reports: Partial<IAnnualReportDto>[], ascending = false): Partial<IAnnualReportDto>[] => {
            return [...reports].sort((a, b) => {
                const dateA = a.generalInformation?.balanceSheetDate ? new Date(a.generalInformation.balanceSheetDate).getTime() : 0;
                const dateB = b.generalInformation?.balanceSheetDate ? new Date(b.generalInformation.balanceSheetDate).getTime() : 0;
                return ascending ? dateA - dateB : dateB - dateA; // ascending = oldest first, descending = most recent first
            });
        };

        // For annual or null report types
        if (selectedReportType === ReportType.ANNUAL || selectedReportType == null) {
            // First, try to find reports with type 'annual' or null
            const annualOrNullReports = this.previousAnnualReportOptions.filter(report =>
                report.setupData?.reportType === ReportType.ANNUAL || report.setupData?.reportType == null
            );

            if (annualOrNullReports.length > 0) {
                // Return the latest among annual/null reports
                const sortedAnnualReports = sortByBalanceSheetDate(annualOrNullReports);
                return sortedAnnualReports[FIRST_ELEMENT_INDEX];
            } else {
                // If no annual/null reports found for annual selectedReportType, get the oldest report of any type
                const sortedAllReports = sortByBalanceSheetDate(this.previousAnnualReportOptions); // ascending = oldest first
                return sortedAllReports[FIRST_ELEMENT_INDEX];
            }
        }

        // For selectedReportType that is not annual or null, get the latest report of any type
        const sortedAllReports = sortByBalanceSheetDate(this.previousAnnualReportOptions);
        return sortedAllReports[FIRST_ELEMENT_INDEX];
    }

}
